import { loadDecisionAnalysisRevision } from './decisionAnalysisClient';
import { awaitAbortableRead, throwIfReadAborted } from '../lib/abortableRead';
import { workspaceReadSignal } from '../lib/workspaceReadSession';
import type {
  AnalysisRevisionReferenceV1,
  DecisionAnalysisSnapshotV1,
} from '../types/decisionAnalysis';

export interface LoadedDecisionAnalysisRevision {
  reference: AnalysisRevisionReferenceV1;
  analysis: DecisionAnalysisSnapshotV1;
}

/** The pointer fields that identify an immutable revision's bytes. */
export type DecisionAnalysisRevisionKey = Pick<
  AnalysisRevisionReferenceV1,
  'analysisId' | 'snapshotPath' | 'contentHash'
>;

/**
 * Bounded in-memory cache of immutable decision-analysis revisions, keyed by
 * viewer, owner, site, analysis id and immutable content identity.
 * An auth-session boundary cancels pending reads and clears resolved bytes.
 *
 * A revision is content-addressed: the same `snapshotPath` + `contentHash`
 * can only ever name the same bytes, so a hit is always exact. The site page
 * re-resolves the current pointers on a 12s poll while any refresh is
 * running; before this cache every poll re-downloaded and re-validated every
 * referenced revision (hundreds of KB each) although the pointers had not
 * moved. A hit also returns the SAME object, so downstream identity checks
 * (`loadedDecisionAnalyses`, the analysis snapshots fed to Path to GO) can
 * keep their references across polls.
 *
 * Only RESOLVED loads are cached — never in-flight promises — so a caller
 * aborting its request can never hand a rejected promise to the next caller.
 */
const MAX_ENTRIES = 64;
const revisionCache = new Map<string, LoadedDecisionAnalysisRevision>();
let cacheSession: AbortSignal | undefined;

export function buildDecisionAnalysisRevisionCacheKey(
  viewerUid: string,
  ownerUid: string | undefined,
  siteId: string,
  key: DecisionAnalysisRevisionKey,
  companyId?: string | null,
): string {
  return JSON.stringify([
    companyId ?? '',
    viewerUid,
    ownerUid ?? viewerUid,
    siteId,
    key.analysisId,
    key.snapshotPath,
    key.contentHash,
  ]);
}

export async function loadDecisionAnalysisRevisionCached(
  siteId: string,
  expected: DecisionAnalysisRevisionKey,
  options: NonNullable<Parameters<typeof loadDecisionAnalysisRevision>[2]> & {
    viewerUid?: string;
    companyId?: string | null;
  } = {},
): Promise<LoadedDecisionAnalysisRevision> {
  const session = workspaceReadSignal();
  if (cacheSession !== session) {
    revisionCache.clear();
    cacheSession = session;
    session.addEventListener('abort', () => revisionCache.clear(), { once: true });
  }
  throwIfReadAborted(options.signal);
  throwIfReadAborted(session);
  const { viewerUid, companyId, ...readOptions } = options;
  // An unscoped caller still gets a verified read, never an auth-bypassing hit.
  const key = viewerUid
    ? buildDecisionAnalysisRevisionCacheKey(viewerUid, options.ownerUid, siteId, expected, companyId)
    : null;
  const hit = key ? revisionCache.get(key) : undefined;
  if (hit) {
    // LRU touch: re-insert so the oldest untouched entry is evicted first.
    revisionCache.delete(key!);
    revisionCache.set(key!, hit);
    return hit;
  }
  const loaded = await awaitAbortableRead(awaitAbortableRead(
    loadDecisionAnalysisRevision(siteId, expected.analysisId, readOptions), options.signal,
  ), session);
  throwIfReadAborted(options.signal);
  throwIfReadAborted(session);
  // Cache only what the caller asked for. The client already rejects a body
  // whose reference disagrees with its analysis; this guards the cache key.
  if (
    key
    && loaded.reference.analysisId === expected.analysisId
    && loaded.reference.snapshotPath === expected.snapshotPath
    && loaded.reference.contentHash === expected.contentHash
  ) {
    revisionCache.set(key, loaded);
    while (revisionCache.size > MAX_ENTRIES) {
      const oldest = revisionCache.keys().next().value;
      if (oldest === undefined) break;
      revisionCache.delete(oldest);
    }
  }
  return loaded;
}

/** Test seam. */
export function clearDecisionAnalysisRevisionCache(): void {
  revisionCache.clear();
}

/** Test seam. */
export function decisionAnalysisRevisionCacheSize(): number {
  return revisionCache.size;
}
